import { Separator } from '@givebutter/primitives/separator';
import { maskDecimal, maskWholeNumber } from '@givebutter/util';
import { OveragesTooltip } from 'assets/js/pages/account-settings/account-about/givebutter-plus/OveragesTooltip';
import { SubscriptionUsage } from 'assets/js/pages/account-settings/account-about/givebutter-plus/SubscriptionUsage';
import { AnchorHashIds } from 'constants/anchor-hash-ids';
import { CurrencySymbol } from 'customHooks/useCurrencySymbol';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { LONG_DATE_FORMAT, formatDate, isoDateFromServer } from 'helpers/dates';
import GivebutterPlusLogo from 'images/givebutter-plus-logo.svg';
import { twJoin } from 'tailwind-merge';

const CurrentPlanCard = () => {
    const { validGivebutterPlusPlan } = useCurrentAccount();

    if (!validGivebutterPlusPlan) return null;

    const {
        tier,
        current_period_end: currentPeriodEnd,
        ends_at: subscriptionEnd,
        is_reporting_contact_overage,
    } = validGivebutterPlusPlan;

    const isGracePeriod = !is_reporting_contact_overage;

    const nextBillDate = currentPeriodEnd
        ? formatDate(isoDateFromServer(currentPeriodEnd), LONG_DATE_FORMAT)
        : undefined;

    return (
        <div
            className="flex w-full scroll-mt-24 flex-col gap-6"
            id={AnchorHashIds.BillingViewGivebutterPlusPlan}
        >
            <img className="max-w-36" src={GivebutterPlusLogo} alt="Givebutter Plus logo" />
            <div className="flex gap-8">
                <SubscriptionUsage />
                <div className="flex min-w-72 flex-col gap-2">
                    <p className="font-medium">Current plan</p>
                    {nextBillDate && !subscriptionEnd && (
                        <p className="text-nowrap text-sm font-medium">
                            Next bill due on {nextBillDate}
                        </p>
                    )}
                    <div className="flex justify-between gap-1 text-nowrap text-sm text-gray-600">
                        <span className="shrink-0">
                            Up to {maskWholeNumber(tier.max_contacts)} contacts
                        </span>
                        <span>
                            <CurrencySymbol />
                            {maskDecimal(tier.amount)}
                        </span>
                    </div>
                    {(tier.overage_pack_count > 0 || tier.unreported_overage_pack_count > 0) && (
                        <div className="flex justify-between gap-1 text-nowrap text-sm text-gray-600">
                            <span className="flex shrink-0 gap-2">
                                Extra 100 contacts ×{' '}
                                {isGracePeriod
                                    ? tier.unreported_overage_pack_count
                                    : tier.overage_pack_count}{' '}
                                <OveragesTooltip />
                            </span>
                            <span className="flex gap-1">
                                <span className={twJoin(isGracePeriod && 'line-through')}>
                                    <CurrencySymbol />
                                    {maskDecimal(
                                        isGracePeriod
                                            ? tier.unreported_overage_total_price
                                            : tier.extra_contacts_price,
                                    )}
                                </span>
                                {isGracePeriod && <span>$0.00</span>}
                            </span>
                        </div>
                    )}
                    <Separator />
                    <div className="flex justify-between gap-1 text-nowrap">
                        <div className="text-sm font-medium text-gray-600">Total</div>
                        <div className="flex flex-col">
                            <div className="text-right font-medium text-gray-900">
                                <CurrencySymbol />
                                {maskDecimal(isGracePeriod ? tier.amount : tier.total_price)}
                            </div>
                            {isGracePeriod && (
                                <span className="text-right text-xs text-gray-600">
                                    for your first month, then <CurrencySymbol />
                                    {maskDecimal(
                                        tier.total_price + tier.unreported_overage_total_price,
                                    )}
                                    /mo
                                </span>
                            )}
                            {!isGracePeriod && tier.interval === 'year' && (
                                <span className="text-right text-xs text-gray-600">
                                    Billed {tier.interval}ly
                                </span>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export { CurrentPlanCard };
